import { formatDate, formatMoney, formatWeekday, pl, t } from "@hakili/i18n";
import { Button, Card, CHIFFRE, Chip, METADONNEE, TEXTE_SECONDAIRE } from "@hakili/ui";
import { Car, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import type {
  AttentionItem,
  DealerBalance,
  Dealership,
  DoorResult,
  Inventory,
  InventoryVehicle,
} from "../session/useSession.js";
import type { DealerDossiersReadAnswer } from "../session/dealer-dossiers.js";
import type { IssuedProforma } from "../session/vehicle-papers.js";
import { dayOf, sinceWords } from "./dossier-dates.js";
import { HakiliFace } from "./hakili-face.js";
import { IssuedNotice, issuedLineOf, RefusalNotice } from "./proforma-outcome.js";
import { ReadFailure } from "./read-failure.js";
import { ReceiptDialog } from "./receipt-dialog.js";
import { EXITED_STAGES, isLive, stageLabel, stageTone } from "./stages.js";

type Dossier = DealerDossiersReadAnswer["dossiers"][number];

/**
 * `P1-DLW-051` (DEALER V2, the Accueil; the iteration-2 frames `design/dealer-v2` `a2ede3b2`; the architect's orders of 2 November 2026): **the day's work, from what the doors serve today.**
 *
 * « Une journée à faire, pas une liste à lire » (the frame's own note): the first task is big, with its one primary button; the others are lines under « Ensuite ». The tasks are DERIVED from served state and nothing is written by hand:
 * the dealership's pending acts (`GET me/attention`: a price-gap decision, a mise à disposition) and the dossiers' served actions (`status.action` of the dossiers reader, `readDealerDossiers`: « Confirmer la réception », « Émettre la facture »;
 * their sentence and their label are the SERVER's).
 *
 * **The order is a fixed PRESENTATION ORDER over served data** (ruling): `ecartPrix`, `confirmerReception`, `emettreProforma`, `miseADisposition`; the oldest first inside a kind. There is no « Ça bloque quelqu'un » label and « Plus tard » is
 * drawn nowhere. The two tiles are the money screen's door (« Solde prépayé », a dash until the ledger answers) and « {n} dossiers attendent votre action » (`counts.byWaitingOn.dealer`, leading to the list).
 *
 * **Not drawn, because no door serves it:** « À recevoir », « Prêtes à financer », the low-balance chip, « Hakili s'occupe pour vous », « Une voiture ne bouge pas », the visit, pièce and call tasks, « Au comptoir ».
 *
 * **Two rulings of the architect (2 November 2026) over the frame:** the greeting stays « Bonjour, {organisation} » until a first name is SERVED (then « Bonjour {prénom} · {organisation} »), because `P0-DLW-001` (an administrator sees which
 * dealership he is in) is a record guarantee a frame cannot remove; and « Vos véhicules en cours » (WF-DLR-01 step 5, canon) STAYS, compact, under the tasks, the served rows as before: the frame's withdrawal of it is a proposition for the founder, not ruled.
 *
 * A read that failed is SAID where it is drawn (`dealer.lecture.dossiers`, `dealer.lecture.alertes`) and never drawn as « nothing waits » or as a zero.
 */

const KIND_ORDER = ["ecartPrix", "confirmerReception", "emettreProforma", "miseADisposition"] as const;
type TaskKind = (typeof KIND_ORDER)[number];

/** DESIGNER R's review of #893 (contrast AA): the dealer's metadata text is the kit's #46536F (7,5:1), the value `dealer-colours.css` declares; the fallback keeps it right wherever that file is not loaded. */
const METADATA_COLOUR = { color: "var(--hk-text-2, #46536f)" } as const;

interface Task {
  readonly key: string;
  readonly kind: TaskKind;
  readonly title: string;
  readonly detail: string;
  readonly since: string;
  readonly buttonLabel: string;
  readonly run: () => void;
}

/** `PRICE-GAP-DEALER`: the pending acts the door lists; each is a task that opens the car. */
const attentionKind = (item: AttentionItem): TaskKind =>
  item.kind === "ecartPrix" ? "ecartPrix" : "miseADisposition";

/** The presentation order, then the oldest first. Pure over served data. */
export function orderTasks(tasks: readonly Task[]): readonly Task[] {
  return [...tasks].sort(
    (a, b) =>
      KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) ||
      (a.since < b.since ? -1 : a.since > b.since ? 1 : 0),
  );
}

/** The drawing's own tile: a label, a value, a line under it; a button when it leads somewhere. */
function Tuile({
  label,
  value,
  suite,
  testId,
  valueTestId,
  suiteTestId,
  onOpen,
}: {
  readonly label: string;
  readonly value: string;
  readonly suite: string;
  readonly testId: string;
  readonly valueTestId?: string;
  readonly suiteTestId: string;
  readonly onOpen: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={testId}
      className="group relative rounded-2xl border border-gray-200 bg-white px-5 py-4 text-left hover:bg-gray-50"
    >
      {/* `P1-DLW-052` (designer R, batch 1): the tile says it leads somewhere. Grey at rest, indigo on hover and on keyboard focus (the kit's tokens); the tile stays one button. */}
      <ChevronRight
        data-chevron=""
        aria-hidden="true"
        className="absolute right-4 top-4 h-5 w-5 text-gray-500 group-hover:text-indigo-600 group-focus-visible:text-indigo-600"
      />
      <span className={`block ${METADONNEE} ${TEXTE_SECONDAIRE}`}>{label}</span>
      <span
        className={`mt-1 block text-2xl font-semibold ${CHIFFRE}`}
        {...(valueTestId === undefined ? {} : { "data-testid": valueTestId })}
      >
        {value}
      </span>
      <span className={`mt-1 block ${METADONNEE}`} style={METADATA_COLOUR} data-testid={suiteTestId}>
        {suite}
      </span>
    </button>
  );
}

export interface AccueilProps {
  /** Not drawn yet: `/me` serves a full name and no first name (the ruling: « Bonjour {prénom} · {organisation} » once one is served). */
  readonly nomComplet: string;
  readonly organisation: string | null;
  /** `T-1`/F2: the cars, the working ones listed under the tasks (WF-DLR-01 step 5). */
  readonly listVehicles: () => Promise<DoorResult<Inventory>>;
  /** `P1-DLW-051`: the dealer dossiers reader (`session/dealer-dossiers.ts`), the list's own. */
  readonly readDossiers: () => Promise<DoorResult<DealerDossiersReadAnswer>>;
  readonly issueProforma: (input: {
    vehicleId: string;
    dossierId: string;
  }) => Promise<DoorResult<IssuedProforma>>;
  readonly confirmReceipt: (
    settlementId: string,
    receivedOn: string,
    idempotencyKey?: string,
  ) => Promise<DoorResult<unknown>>;
  /** `T-1d`: the tile's figure is the ledger's sum, by the same door « Solde » reads. */
  readonly readBalance: () => Promise<DoorResult<DealerBalance>>;
  /** `K-2` (resumed), `M3`/`M4`: the sanctions the platform holds, read on the dealership; one banner each. */
  readonly readDealership: () => Promise<DoorResult<Dealership>>;
  /** `PRICE-GAP-DEALER`, M3: what waits for the dealership's hand. */
  readonly readAttention?: () => Promise<DoorResult<readonly AttentionItem[]>>;
  readonly naviguer: (to: string) => void;
}

export function Accueil({
  organisation,
  listVehicles,
  readDossiers,
  issueProforma,
  confirmReceipt,
  readBalance,
  readDealership,
  readAttention,
  naviguer,
}: AccueilProps): React.JSX.Element {
  const [vehicles, setVehicles] = useState<readonly InventoryVehicle[] | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [dossiers, setDossiers] = useState<DoorResult<DealerDossiersReadAnswer> | null>(null);
  const [today, setToday] = useState<string | null>(null);
  const [now, setNow] = useState<string | null>(null);
  const [attention, setAttention] = useState<readonly AttentionItem[]>([]);
  const [alertesEchec, setAlertesEchec] = useState<string | null>(null);
  const [alertesLues, setAlertesLues] = useState(readAttention === undefined);
  const [balance, setBalance] = useState<number | null>(null);
  const [sanctions, setSanctions] = useState<{ demotedUntil: string | null; suspended: boolean } | null>(
    null,
  );
  const [taskError, setTaskError] = useState<{ readonly key: string; readonly message: string } | null>(null);
  const [receipt, setReceipt] = useState<{ readonly settlementId: string; readonly key: string } | null>(
    null,
  );
  const [busy, setBusy] = useState<string | null>(null);
  /** A47: the one-tap proforma is never silent. What the door answered, said in words (the task leaves the list when the dossiers are read again). */
  const [issued, setIssued] = useState<{ readonly who: string; readonly line: string } | null>(null);

  const loadDossiers = useCallback(async (): Promise<void> => {
    const answer = await readDossiers();
    // `OD-17`: the instant is the SERVER's, carried by the dossiers answer itself (`asOf`, P1-DOS-078); an answer without it dates nothing here.
    const instant = answer.ok && answer.value.asOf !== undefined ? answer.value.asOf : null;
    setNow(instant);
    setToday(instant === null ? null : dayOf(instant));
    setDossiers(answer);
  }, [readDossiers]);

  useEffect(() => {
    void loadDossiers();
  }, [loadDossiers]);

  /** `A4` part 2: a refused vehicles read is SAID (« vos chiffres ») and the list is not drawn, never an empty list that reads « no vehicles ». */
  const [vehiclesNonce, setVehiclesNonce] = useState(0);
  useEffect(() => {
    let vivant = true;
    void listVehicles().then((answer) => {
      if (!vivant) return;
      if (answer.ok) {
        setVehicles(answer.value.vehicles);
        setRefus(null);
      } else setRefus(answer.message);
    });
    return () => {
      vivant = false;
    };
  }, [listVehicles, vehiclesNonce]);

  const [attentionNonce, setAttentionNonce] = useState(0);
  useEffect(() => {
    if (readAttention === undefined) return;
    let vivant = true;
    void readAttention().then((answer) => {
      if (!vivant) return;
      if (answer.ok) {
        setAttention(answer.value);
        setAlertesEchec(null);
      } else {
        setAlertesEchec(answer.message);
      }
      setAlertesLues(true);
    });
    return () => {
      vivant = false;
    };
  }, [readAttention, attentionNonce]);

  useEffect(() => {
    let vivant = true;
    // The banners are the door's facts; a refused read prints none (R81), never an invented state.
    void readDealership().then((answer) => {
      if (vivant && answer.ok)
        setSanctions({ demotedUntil: answer.value.demotedUntil, suspended: answer.value.suspended });
    });
    return () => {
      vivant = false;
    };
  }, [readDealership]);

  useEffect(() => {
    let vivant = true;
    // The dash stays while the door has not answered a figure (`R81`): the tile never sums anything itself, and a refused read is not a zero.
    void readBalance().then((answer) => {
      if (vivant && answer.ok) setBalance(answer.value.balance);
    });
    return () => {
      vivant = false;
    };
  }, [readBalance]);

  const dash = t("dealer.accueil.absent");

  const dossierTask = (dossier: Dossier): Task | null => {
    const action = dossier.status.action;
    if (action === null) return null;
    const kind: TaskKind = action.code;
    const key = action.code === "confirmerReception" ? action.target.settlementId : action.target.dossierId;
    return {
      key: `${kind}-${key}`,
      kind,
      title: dossier.status.nextText ?? dossier.status.whereText,
      detail: `${dossier.client.name} · ${dossier.vehicle.label}`,
      since: dossier.since,
      buttonLabel: action.label,
      run: () => {
        setTaskError(null);
        setIssued(null);
        if (action.code === "confirmerReception") {
          setReceipt({ settlementId: action.target.settlementId, key: globalThis.crypto.randomUUID() });
          return;
        }
        const taskKey = `${kind}-${key}`;
        setBusy(taskKey);
        void issueProforma({ vehicleId: action.target.vehicleId, dossierId: action.target.dossierId }).then(
          async (done) => {
            setBusy(null);
            if (!done.ok) {
              setTaskError({ key: taskKey, message: done.message });
              return;
            }
            setIssued({
              who: `${dossier.client.name} · ${dossier.vehicle.label}`,
              line: issuedLineOf(done.value),
            });
            await loadDossiers();
          },
        );
      },
    };
  };

  const attentionTask = (item: AttentionItem): Task => {
    const kind = attentionKind(item);
    return {
      key: `${kind}-${item.vehicleId}`,
      kind,
      title:
        kind === "ecartPrix"
          ? t("dealer.accueil.prioriteEcartPrix")
          : t("dealer.accueil.prioriteMiseADisposition"),
      detail: `${item.title} · ${item.stockReference}`,
      since: item.since,
      buttonLabel: t("dealer.accueil.ouvrirVehicule"),
      run: () => {
        naviguer(`/vehicules/${item.vehicleId}`);
      },
    };
  };

  const dossiersOk = dossiers?.ok === true ? dossiers.value : null;
  const tasks = orderTasks([
    ...attention.map(attentionTask),
    ...(dossiersOk === null ? [] : dossiersOk.dossiers.flatMap((one) => dossierTask(one) ?? [])),
  ]);
  const [lead, ...rest] = tasks;

  const sinceOf = (task: Task): string | null => sinceWords(task.since, today);
  const dossiersFailed = dossiers !== null && !dossiers.ok;
  const anyFailed = dossiersFailed || alertesEchec !== null;
  const settled = dossiers !== null && alertesLues;
  const day = now === null ? null : formatWeekday(new Date(now));
  const count =
    !settled || anyFailed
      ? null
      : tasks.length === 0
        ? t("dealer.accueil.rienAFaire")
        : pl(tasks.length, t("dealer.accueil.choseAFaire"), t("dealer.accueil.chosesAFaire"));
  const subtitle = [day, count].filter((part): part is string => part !== null).join(" · ");
  const live = (vehicles ?? []).filter((car) => isLive(car.stage));
  const exited = (vehicles ?? []).filter((car) => EXITED_STAGES.has(car.stage));

  const taskBody = (task: Task, hero: boolean): React.JSX.Element => (
    <>
      <div className="min-w-0 flex-1">
        <p
          className={
            hero ? "m-0 text-xl font-semibold text-gray-900" : "m-0 text-sm font-semibold text-gray-900"
          }
        >
          {task.title}
        </p>
        <p className={`m-0 mt-1 ${hero ? "text-sm" : METADONNEE} ${TEXTE_SECONDAIRE}`}>
          {task.detail}
          {sinceOf(task) === null ? "" : ` · ${sinceOf(task) ?? ""}`}
        </p>
        {taskError?.key === task.key ? (
          <RefusalNotice message={taskError.message} testId="accueil-tache-erreur" />
        ) : null}
      </div>
      <Button
        variant={hero ? "primary" : "secondary"}
        size={hero ? "md" : "sm"}
        disabled={busy === task.key}
        onClick={task.run}
        data-testid="accueil-tache-bouton"
      >
        {task.buttonLabel}
      </Button>
    </>
  );

  return (
    <div className="flex flex-col gap-6" data-testid="accueil">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="accueil-titre">
          {organisation === null
            ? t("dealer.accueil.bonjour")
            : `${t("dealer.accueil.bonjour")}, ${organisation}`}
        </h1>
        {subtitle === "" ? null : (
          <p className={`mt-1.5 text-sm ${TEXTE_SECONDAIRE}`} data-testid="accueil-resume">
            {subtitle}
          </p>
        )}
      </div>

      {/* `K-2` (resumed), `M3` (b) and (a): the suspension's banner (his open files untouched, the contact his relation seat), then the demotion's , each the door's own fact. */}
      {sanctions?.suspended === true ? (
        <div
          role="alert"
          className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
          data-testid="accueil-suspendu"
        >
          {t("dealer.accueil.suspendu")}
        </div>
      ) : sanctions?.demotedUntil !== null && sanctions?.demotedUntil !== undefined ? (
        <div
          className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700"
          data-testid="accueil-retrograde"
        >
          {t("dealer.accueil.retrograde").replace("{date}", formatDate(new Date(sanctions.demotedUntil)))}
        </div>
      ) : null}

      {dossiersFailed ? (
        <ReadFailure
          what="dealer.lecture.dossiers"
          message={dossiers.message}
          onRetry={() => {
            setDossiers(null);
            void loadDossiers();
          }}
          testId="accueil-dossiers-echec"
          retryTestId="accueil-dossiers-echec-reessayer"
        />
      ) : null}
      {alertesEchec === null ? null : (
        <ReadFailure
          what="dealer.lecture.alertes"
          message={alertesEchec}
          onRetry={() => {
            setAttentionNonce((one) => one + 1);
          }}
          testId="accueil-alertes-echec"
          retryTestId="accueil-alertes-echec-reessayer"
        />
      )}

      {refus === null ? null : (
        <ReadFailure
          what="dealer.lecture.chiffres"
          message={refus}
          onRetry={() => {
            setVehiclesNonce((one) => one + 1);
          }}
          testId="accueil-refus"
          retryTestId="accueil-refus-reessayer"
        />
      )}

      {/* ------------------------------------------ the two tiles the doors serve */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="accueil-chiffres">
        <Tuile
          label={t("dealer.accueil.solde")}
          value={balance === null ? dash : formatMoney(balance)}
          suite={t("dealer.accueil.soldeExpertises")}
          testId="accueil-tuile-solde"
          valueTestId="accueil-solde"
          suiteTestId="accueil-tuile-solde-suite"
          onOpen={() => {
            naviguer("/solde");
          }}
        />
        {dossiersOk === null ? null : (
          <Tuile
            label={t("dealer.dossiers.titre")}
            value={String(dossiersOk.counts.byWaitingOn.dealer)}
            suite={
              dossiersOk.counts.byWaitingOn.dealer === 1
                ? t("dealer.accueil.dossierAttend")
                : t("dealer.accueil.dossiersAttendent")
            }
            testId="accueil-tuile-dossiers"
            suiteTestId="accueil-tuile-dossiers-suite"
            onOpen={() => {
              naviguer("/dossiers");
            }}
          />
        )}
      </div>

      {issued === null ? null : (
        <IssuedNotice who={issued.who} line={issued.line} testId="accueil-proforma-emise" />
      )}

      {/* ------------------------------------------------- the day's work */}
      {lead === undefined ? null : (
        <div className="flex flex-col gap-3">
          <section
            className="flex flex-wrap items-center gap-4 rounded-2xl border border-gray-200 border-l-4 border-l-amber-500 bg-white px-6 py-5"
            data-testid="accueil-tache-premiere"
            data-tache={lead.key}
          >
            {taskBody(lead, true)}
          </section>
          {rest.length === 0 ? null : (
            <>
              <h2
                className={`m-0 mt-2 ${METADONNEE} font-semibold uppercase tracking-wide ${TEXTE_SECONDAIRE}`}
                data-testid="accueil-ensuite"
              >
                {`${t("dealer.accueil.ensuite")} · ${String(rest.length)}`}
              </h2>
              <ul className="m-0 flex list-none flex-col gap-3 p-0">
                {rest.map((task) => (
                  <li
                    key={task.key}
                    data-testid="accueil-tache"
                    data-tache={task.key}
                    className="flex flex-wrap items-center gap-4 rounded-2xl border border-gray-200 bg-white px-5 py-3.5"
                  >
                    {taskBody(task, false)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {/* ------------------------- « Vos véhicules en cours » (WF-DLR-01 step 5, canon): compact, under the tasks, the served rows */}
      {vehicles === null ? null : (
        <Card>
          <div className="flex items-center justify-between gap-3">
            <h2 className="m-0 text-sm font-semibold">{t("dealer.accueil.enCours")}</h2>
            <button
              type="button"
              onClick={() => {
                naviguer("/vehicules");
              }}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
              data-testid="accueil-voir-catalogue"
            >
              {t("dealer.accueil.voirCatalogue")}
            </button>
          </div>
          <ul className="m-0 mt-2 list-none divide-y divide-gray-50 p-0" data-testid="accueil-en-cours">
            {live.map((car) => (
              <li key={car.id}>
                <button
                  type="button"
                  onClick={() => {
                    naviguer(`/vehicules/${car.id}`);
                  }}
                  className="flex w-full items-center gap-3 py-2 text-left hover:bg-gray-50"
                  data-testid={`accueil-ligne-${car.id}`}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100">
                    <Car className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900">{car.title}</span>
                    {/* The buyer, or the plain fact that nobody has asked: a fact (`hasBuyer`), so the row says « un acheteur » and no name the door does not carry. */}
                    <span className={`block ${METADONNEE} ${TEXTE_SECONDAIRE}`}>
                      {car.hasBuyer ? t("dealer.vehicules.acheteur") : t("dealer.accueil.aucunContact")}
                    </span>
                  </span>
                  <Chip tone={stageTone(car.stage)}>{stageLabel(car.stage)}</Chip>
                </button>
              </li>
            ))}
            {live.length === 0 ? (
              <li className="py-2 text-sm text-gray-500" data-testid="accueil-en-cours-vide">
                {t("dealer.accueil.aucunVehicule")}
              </li>
            ) : null}
          </ul>
          {exited.length === 0 ? null : (
            <p className={`m-0 mt-2 ${METADONNEE}`} style={METADATA_COLOUR} data-testid="accueil-sorties">
              {pl(exited.length, t("dealer.accueil.sortie"), t("dealer.accueil.sorties"))}{" "}
              {t("dealer.accueil.sortiesSuite")}
            </p>
          )}
        </Card>
      )}

      {/* `P1-DLW-052` (designer R, batch 1): on an empty day the card is LAST, under the served cars list and farthest from the money tiles; Hakili at rest, R's title and sentence, ONE primary. Never over a failed read. */}
      {lead === undefined && settled && !anyFailed ? (
        <div
          className="rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center"
          data-testid="accueil-priorites-vides"
        >
          <HakiliFace />
          <h2 className="m-0 text-lg font-semibold text-gray-900">{t("dealer.accueil.rienAujourdhui")}</h2>
          <p className="mx-auto m-0 mt-2 max-w-md text-sm text-gray-500">
            {t("dealer.accueil.rienAujourdhuiCorps")}
          </p>
          <div className="mt-4">
            <Button
              variant="primary"
              onClick={() => {
                naviguer("/vehicules");
              }}
              data-testid="accueil-voir-voitures"
            >
              {t("dealer.accueil.voirMesVoitures")}
            </Button>
          </div>
        </div>
      ) : null}

      {receipt === null ? null : (
        <ReceiptDialog
          key={receipt.key}
          today={today}
          onCancel={() => {
            setReceipt(null);
          }}
          onConfirm={(receivedOn) => confirmReceipt(receipt.settlementId, receivedOn, receipt.key)}
          onDone={() => {
            setReceipt(null);
            void loadDossiers();
          }}
        />
      )}
    </div>
  );
}
